"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useMemo } from "react";
import { callstackSlice } from "#/features/callstack/model/callstackSlice";
import { projectSlice } from "#/features/project/model/projectSlice";
import { useHasMounted, usePrevious } from "#/shared/hooks";
import { usePlaygroundRoute } from "#/shared/hooks/usePlaygroundRoute";
import { buildPlaygroundPath } from "#/shared/lib/playgroundRoute";
import { getLastPlaygroundPath, isValidLastPlaygroundPath } from "#/shared/local-storage/playgroundPath";
import { useAppDispatch } from "#/store/hooks";
import { isPlaygroundView, type PlaygroundView, usePlaygroundViewParam } from "./usePlaygroundViewParam";
export type { PlaygroundView } from "./usePlaygroundViewParam";

/**
 * Manages the mobile playground phase via the `?view=` query parameter.
 *
 * - **Implicit code with a slug**: if `view` is absent, shallow `replace` adds `?view=code`.
 * - **Tab changes**: shallow `router.replace` (no extra history entries per tab tap).
 * - Under App Router pilot: uses {@link usePlaygroundRoute} for slug + `?view=` sync.
 */
export const useMobilePlaygroundView = () => {
  const $ = _c(31);
  const route = usePlaygroundRoute();
  const dispatch = useAppDispatch();
  const hasMounted = useHasMounted();
  const {
    view,
    setView
  } = usePlaygroundViewParam();
  const hasProjectSlug = route !== null && route.slug.length > 0;
  let t0;
  bb0: {
    if (view && isPlaygroundView(view)) {
      t0 = view;
      break bb0;
    }
    if (hasProjectSlug) {
      t0 = "code";
      break bb0;
    }
    if (!hasMounted) {
      t0 = "browse";
      break bb0;
    }
    t0 = isValidLastPlaygroundPath(getLastPlaygroundPath()) ? "code" : "browse";
  }
  const currentView = t0;
  const previousView = usePrevious(currentView);
  let t1;
  if ($[0] !== hasProjectSlug || $[1] !== route?.basePath || $[2] !== setView || $[3] !== view) {
    t1 = () => {
      if (route?.basePath === undefined) {
        return;
      }
      if (!hasProjectSlug) {
        return;
      }
      if (view !== "") {
        return;
      }
      setView("code", {
        replace: true
      });
    };
    $[0] = hasProjectSlug;
    $[1] = route?.basePath;
    $[2] = setView;
    $[3] = view;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const t2 = route?.basePath;
  let t3;
  if ($[5] !== hasProjectSlug || $[6] !== setView || $[7] !== t2 || $[8] !== view) {
    t3 = [hasProjectSlug, view, setView, t2];
    $[5] = hasProjectSlug;
    $[6] = setView;
    $[7] = t2;
    $[8] = view;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  useEffect(t1, t3);
  let t4;
  if ($[10] !== setView) {
    t4 = (targetView, pathName) => {
      setView(targetView, {
        replace: true,
        pathName
      });
    };
    $[10] = setView;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const navigateTo = t4;
  let t5;
  if ($[12] !== navigateTo) {
    t5 = () => navigateTo("browse");
    $[12] = navigateTo;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const goToBrowse = t5;
  let t6;
  if ($[14] !== dispatch || $[15] !== navigateTo || $[16] !== route) {
    t6 = projectSlug => {
      const pathName_0 = projectSlug && route ? buildPlaygroundPath(route.basePath, [projectSlug]) : undefined;
      if (projectSlug) {
        dispatch(projectSlice.actions.loadStart());
      }
      navigateTo("code", pathName_0);
    };
    $[14] = dispatch;
    $[15] = navigateTo;
    $[16] = route;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const goToCode = t6;
  let t7;
  if ($[18] !== navigateTo) {
    t7 = () => navigateTo("results");
    $[18] = navigateTo;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  const goToResults = t7;
  let t8;
  let t9;
  if ($[20] !== currentView || $[21] !== dispatch || $[22] !== previousView) {
    t8 = () => {
      if (previousView === "results" && currentView !== "results") {
        dispatch(callstackSlice.actions.setIsPlaying(false));
      }
    };
    t9 = [currentView, previousView, dispatch];
    $[20] = currentView;
    $[21] = dispatch;
    $[22] = previousView;
    $[23] = t8;
    $[24] = t9;
  } else {
    t8 = $[23];
    t9 = $[24];
  }
  useEffect(t8, t9);
  let t10;
  if ($[25] !== currentView || $[26] !== goToBrowse || $[27] !== goToCode || $[28] !== goToResults || $[29] !== hasProjectSlug) {
    t10 = {
      currentView,
      hasProjectSlug,
      goToBrowse,
      goToCode,
      goToResults
    };
    $[25] = currentView;
    $[26] = goToBrowse;
    $[27] = goToCode;
    $[28] = goToResults;
    $[29] = hasProjectSlug;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  return t10 as const;
};